import React, { useEffect, Fragment } from 'react';
import { graphql } from 'react-relay';

import type { EditorActions } from '@atlaskit/editor-core';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { usePublicLinkPageDataProvider } from '@confluence/public-links/entry-points/usePublicLinkPageDataProvider.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getPublishButtonDisabledReason } from '@confluence/editor-features/entry-points/utils/getPublishButtonDisabledReason.ts';
import { deleteDraft } from '@confluence/editor-features/entry-points/deleteDraft.ts';
import { useLabels } from '@confluence/editor-features/entry-points/useLabels.ts';
import { type PublishProps } from '@confluence/editor-features/entry-points/PublishProps.ts';
import type { RetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent.ts';
import { MainButtons } from '@confluence/editor-main-buttons';
import {
	EllipsisDropdownComponent,
	Buttons,
} from '@confluence/editor-ellipsis-dropdown/entry-points/EditorEllipsisDropdown.ts';
import { LivePagesEllipsisDropdownComponent } from '@confluence/editor-ellipsis-dropdown/entry-points/LivePagesEllipsisDropdown.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { useNativeCollabConnected } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { EDITOR_CLOSE_BUTTON_CLICK } from '@confluence/browser-metrics/entry-points/viewPageTransitionSourceConstants.ts';
import { setViewTransitionSource } from '@confluence/browser-metrics/entry-points/viewTransitionSource.ts';
import { withPageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container/entry-points/withPageTreeStateUpdater.ts';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import { useBlogTreeActions } from '@confluence/blog-tree-store/entry-points/useBlogTreeStore.ts';
import { getParentOfDraft } from '@confluence/editor-features/entry-points/utils/getParentOfDraft.ts';
import { useWillDeleteBlankDraftOnClose } from '@confluence/editor-features/entry-points/useWillDeleteBlankDraftOnClose.ts';
import { useEmojiTitleCoverPictureAndContentState } from '@confluence/editor-features/entry-points/useEmojiTitleCoverPictureAndContentState.ts';
import { useRestrictions } from '@confluence/editor-features/entry-points/useRestrictions.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

export type EditorPrimaryToolbarComponentsProps = {
	forceDisabled?: boolean;
	userOffline: boolean;
	collabLimitReached: boolean;
	contentId: string;
	contentType: string;
	isContentCreationPanelOpen: boolean;
	isUnpublishedDraft: boolean;
	onClickPublish: (arg: PublishProps) => () => void;
	onClickOpenContentCreationPanel: () => void;
	onPreviewClicked: (arg?: any) => void;
	saveDraft: (arg?: any) => Promise<void>;
	spaceKey: string;
	title: string | null;
	showRevertDialog: () => void;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	isPublishingContent: boolean;
	editorActions: EditorActions;
	isSingleUserSession: boolean;
	navigateOnClose: (args?: { isDiscarding?: boolean }) => void;
	triggerAnalyticsEvent: (arg: string, extraData?: { [key: string]: any }) => void;
	shouldRenderPreview: boolean;
	shouldShowRecentSpotlight: boolean;
	checkIfConditionPreventingPublish?: () => Promise<boolean>;
	flags: FlagsStateContainer;
	viewMode?: boolean;
	hasEditPermission?: boolean;
	pageTreeStateUpdater: PageTreeStateUpdater;
	ellipsisEditorActions?: {
		canUndo?: boolean;
		canRedo?: boolean;
		undo?: () => boolean;
		redo?: () => boolean;
		activateFindReplace?: () => boolean;
	};
	allowInlineComments?: boolean;
	allowPageComments?: boolean;
};

const EditorPrimaryToolbarComponentsFunction = ({
	forceDisabled,
	userOffline,
	collabLimitReached,
	contentId,
	contentType,
	isContentCreationPanelOpen,
	isUnpublishedDraft,
	onClickPublish,
	onClickOpenContentCreationPanel,
	onPreviewClicked,
	saveDraft,
	spaceKey,
	title,
	showRevertDialog,
	retrieveEditorTitleAndContent,
	isPublishingContent,
	editorActions,
	isSingleUserSession,
	navigateOnClose,
	triggerAnalyticsEvent,
	shouldRenderPreview,
	shouldShowRecentSpotlight,
	flags,
	viewMode,
	pageTreeStateUpdater,
	hasEditPermission,
	ellipsisEditorActions,
	allowInlineComments = true,
	allowPageComments = true,
}: EditorPrimaryToolbarComponentsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const readFragmentInterop = useReadFragmentInterop({
		fragmentSpec: graphql`
			fragment EditorPrimaryToolbarComponentsGetDraftParentFragment on Content {
				id
				ancestors {
					id
				}
				__typename
			}
		`,
	});

	const {
		emojiCoverPictureAndContentStateSubscription,
		hasEmoji,
		hasCoverPicture,
		hasContentState,
	} = useEmojiTitleCoverPictureAndContentState();

	const { hasRestrictionsSubscription, hasRestrictions } = useRestrictions();
	const publicLinkData = usePublicLinkPageDataProvider({
		pageId: contentId,
		fetchPolicy: 'cache-and-network',
	});

	const hasLabels = useLabels(contentId);

	const [{ collabEditConnected }] = useNativeCollabConnected();

	const shouldDisableActions = isPublishingContent || userOffline || !!forceDisabled;
	const areMainButtonsDisabled = shouldDisableActions || publicLinkData.isLoading;

	const isPublishDisabledForPublishModal = shouldDisableActions || Boolean(collabLimitReached);

	const hasTitle = Boolean(title);
	const hasBody = !editorActions.isDocumentEmpty();
	const { forceRefetchBlogTree } = useBlogTreeActions();

	const willDeleteBlankDraftOnClose = useWillDeleteBlankDraftOnClose({
		isUnpublishedDraft,
		isSingleUserSession,
		hasTitle,
		hasBody,
		hasEmoji,
		hasCoverPicture,
		hasContentState,
		hasRestrictions,
		hasLabels,
	});

	const { setOnboardingState } = useSetOnboardingState();
	// Data is not used so we don't worry about errors here
	useEffect(() => {
		for (const error of publicLinkData.errors) {
			markErrorAsHandled(error);
		}
	}, [publicLinkData.errors]);

	const publishButtonDisabledReason = getPublishButtonDisabledReason({
		title,
		isPublishingContent,
		userOffline,
		forceDisabled,
	});

	const onClose = async () => {
		if (contentType === 'page') {
			setViewTransitionSource(EDITOR_CLOSE_BUTTON_CLICK);
		}
		if (shouldShowRecentSpotlight) {
			void setOnboardingState({
				key: EDITOR_ONBOARDING_STATE_KEYS.RECENT_SPOTLIGHT_OPEN,
				value: 'true',
			});
		}

		if (willDeleteBlankDraftOnClose) {
			triggerAnalyticsEvent('close', {
				discarded: true,
			});
			void deleteDraft({
				contentId,
				deleteOnlyIfBlank: true,
			}).then(async ({ data }) => {
				if (contentType === 'blogpost') {
					// refreshes blog tree on discard close of blog draft
					forceRefetchBlogTree();
				}
				triggerAnalyticsEvent('draftDiscarded', {
					wasDeleted: !!data?.deleteContent?.wasDeleted,
				});
				const parentId = await getParentOfDraft(contentId, readFragmentInterop);
				if (parentId) {
					pageTreeStateUpdater.setForceFetchPageId(parentId);
				} else {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'notFound',
							actionSubject: 'discardedDraftParent',
							objectType: contentType,
							objectId: contentId,
						},
					}).fire();
				}
			});
			navigateOnClose({ isDiscarding: true });
		} else {
			triggerAnalyticsEvent('close');
			void saveDraft({
				isOnClose: true,
			}).then(() => navigateOnClose());
		}
	};

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

	const allowedEllipsisButtons = !isCompanyHub
		? [
				Buttons.PUBLISH_WITHOUT_NOTIFY,
				Buttons.DELETE_DRAFT,
				Buttons.EDIT_LABELS,
				Buttons.MOVE_PAGE,
				Buttons.PREVIEW,
				Buttons.RESOLVED_COMMENTS,
				Buttons.TEMPLATE_BROWSER,
				Buttons.VIEW_CHANGES,
				Buttons.SWITCH_TO_LIVE_EDIT,
				Buttons.CLASSIFY_CONTENT,
			]
		: [Buttons.DELETE_DRAFT, Buttons.PREVIEW, Buttons.RESOLVED_COMMENTS, Buttons.VIEW_CHANGES];

	const isLivePage = useIsCurrentPageLive();
	const isEmbedded = useIsEmbeddedConfluence_DO_NOT_USE();
	const showEllipsisDropdown = !isEmbedded;

	const ellipsisDropdownProps = {
		contentId,
		contentType,
		isDisabled: areMainButtonsDisabled || userOffline || collabLimitReached,
		isUnpublishedDraft,
		isShowingLabelsDialog: false,
		onPreviewClicked,
		onClickPublish,
		retrieveEditorTitleAndContent,
		saveDraft,
		tooltipDisabledState: publishButtonDisabledReason,
		spaceKey,
		isContentCreationPanelShown: isContentCreationPanelOpen,
		onClickOpenContentCreationPanel,
		showRevertDialog,
		allowedEllipsisButtons,
		hasEditPermission,
		editorActions: ellipsisEditorActions,
	};

	// allowCopyLink and allowRestrictions are contingent on if user is not in Company hub space
	// Only inline comments button is whitelisted for Company hub editors to remain consistent with whitelisted actions in the EllipsisDropdown
	return (
		<Fragment>
			{emojiCoverPictureAndContentStateSubscription}
			{hasRestrictionsSubscription}
			<MainButtons
				allowWebItems
				allowCopyLink={!isCompanyHub && !isEmbedded}
				allowInlineComments={allowInlineComments}
				allowPageComments={allowPageComments}
				allowRestrictions={!isCompanyHub && !isEmbedded}
				allowStar={isLivePage && !isEmbedded}
				allowWatch={isLivePage && !isEmbedded}
				areMainButtonsDisabled={areMainButtonsDisabled}
				contentId={contentId}
				contentType={contentType}
				isPublishDisabled={
					!title ||
					!title?.trim() ||
					areMainButtonsDisabled ||
					collabLimitReached ||
					!collabEditConnected
				}
				isCloseButtonDisabled={shouldDisableActions}
				isPublishDisabledForPublishModal={isPublishDisabledForPublishModal}
				isUnpublishedDraft={isUnpublishedDraft}
				discardOnCloseEnabled={willDeleteBlankDraftOnClose}
				onClose={onClose}
				onClickPreview={onPreviewClicked}
				onClickPublish={onClickPublish}
				publishButtonDisabledReason={publishButtonDisabledReason}
				isPublishingContent={isPublishingContent}
				retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
				shouldRenderPreview={shouldRenderPreview}
				flags={flags}
				saveDraft={saveDraft}
				title={title}
				subsequentNode={
					showEllipsisDropdown &&
					(isLivePage ? (
						<LivePagesEllipsisDropdownComponent
							contentId={contentId}
							spaceKey={spaceKey}
							retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
							onClickPublish={onClickPublish}
							saveDraft={saveDraft}
							isViewMode={viewMode && !hasEditPermission}
							onClickOpenContentCreationPanel={onClickOpenContentCreationPanel}
							isDisabled={areMainButtonsDisabled || userOffline || collabLimitReached}
						/>
					) : (
						//@ts-ignore
						<EllipsisDropdownComponent {...ellipsisDropdownProps} />
					))
				}
				spaceKey={spaceKey}
				editorActions={editorActions}
				viewMode={viewMode}
			/>
		</Fragment>
	);
};
export const EditorPrimaryToolbarComponents: React.FC<
	Omit<EditorPrimaryToolbarComponentsProps, 'pageTreeStateUpdater'>
> = withPageTreeStateUpdater(EditorPrimaryToolbarComponentsFunction);
